import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

/**
 * Searchable member dropdown - isolated component so typing doesn't re-render parent.
 */
const MemberSearchSelect = ({ members, value, onChange, disabled, label = 'Member ID *' }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = members.find((m) => m.member_id === value);
  const displayValue = open ? search : (selected ? `${selected.member_id} – ${selected.company_name || selected.individual_name || selected.email || 'Unknown'}` : '');

  const searchLower = search.toLowerCase();
  const filtered = searchLower
    ? members.filter((m) => {
        const label = `${m.member_id} ${m.company_name || ''} ${m.individual_name || ''} ${m.email || ''}`.toLowerCase();
        return label.includes(searchLower);
      })
    : members;

  return (
    <div ref={ref} className="relative">
      <Label>{label}</Label>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search or select a member..."
          value={displayValue}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
            if (!e.target.value) onChange('');
          }}
          onFocus={() => setOpen(true)}
          className="pl-9 pr-9"
          disabled={disabled}
        />
        <ChevronDown
          className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 cursor-pointer"
          onClick={() => setOpen(!open)}
        />
      </div>
      {open && (
        <div
          className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto border rounded-md bg-white shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          {filtered.map((m) => (
            <div
              key={m.member_id}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              onClick={() => {
                onChange(m.member_id);
                setSearch('');
                setOpen(false);
              }}
            >
              <span className="font-mono">{m.member_id}</span>
              <span className="text-gray-600 ml-2">
                &ndash; {m.company_name || m.individual_name || m.email || 'Unknown'}
              </span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-sm text-gray-500 text-center">No members found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MemberSearchSelect;
